/**
 * `file` — `FileValue` (`specs/M2-tier2-column-types.md` §5.4, `[09§A.7 file]`, `[cvs§2 file]`).
 *
 * `value` is `{"files":[…]}` and the entries come in **two** shapes, both measured in the
 * 2026-09-10 snapshot (246 entries across 175 populated cells):
 *
 * | `fileType` | keys | count |
 * |---|---|---|
 * | `LINK` | `name`, `linkToFile`, `createdAt`, `createdBy` (+ `fileId` on 27) | 179 |
 * | `ASSET` | `name`, `assetId`, `isImage`, `fileType`, `createdAt`, `createdBy` | 67 |
 *
 * `isImage` is the **string** `"true"` / `"false"` `[cvs§4.4]` and `createdAt` is epoch
 * **milliseconds**, not an ISO stamp. `DOC` (`fileType: "DOC"`) is documented but never
 * appeared on this account.
 *
 * **Writes.** *"Files can only be added through the `add_file_to_column` mutation. The JSON
 * column value mutations only support the `clear_all` operation."* So `writable` is false and
 * `{"files": {"clear_all": true}}` is the one accepted payload, handled by the service.
 *
 * **`text`.** monday joins the assets' `protected_static` URLs with `", "`; those URLs are on
 * *monday's* host and resolve to nothing here, so ours joins **our own absolute** asset URLs —
 * `${APP_URL}/files/<assetId>`, the route `apps/api/src/routes/files.ts` serves — and a `LINK`
 * entry's `linkToFile` verbatim. Absolute, not relative, because an integration reading
 * `ColumnValue.text` has to be able to download the file (coordinator's ruling, round 5l); the
 * asset keeps monday's own `id`, `name` and `size`, so only the origin differs. Recorded as a
 * deliberate deviation in `specs/M2-tier2-column-types.md` §7 and
 * `tools/conformance/deviations.yaml`.
 *
 * The origin comes from `setAssetBaseUrl()` (the api calls it at boot), else `APP_URL` from the
 * environment, else `ctx.assetUrl` if a caller supplies one; with none of the three it falls
 * back to the relative path, which is still correct inside the browser.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
import { fileFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const fileEntrySchema = z.looseObject({
    name: z.string(),
    /** `ASSET` | `LINK` | `DOC` `[cvs§2 file]`. */
    fileType: z.string(),
    assetId: z.union([z.number(), z.string()]).optional(),
    /** The **string** `"true"` / `"false"` in monday's own JSON `[cvs§4.4]`. */
    isImage: z.union([z.boolean(), z.string()]).optional(),
    linkToFile: z.string().optional(),
    fileId: z.string().optional(),
    /** Epoch **milliseconds**. */
    createdAt: z.number().optional(),
    createdBy: z.union([z.string(), z.number()]).optional(),
});
export const fileValueSchema = z.looseObject({ files: z.array(fileEntrySchema) });
export const fileSettingsSchema = z.looseObject({ hide_footer: z.boolean().optional() });
export function isImageEntry(entry) {
    return entry.isImage === true || entry.isImage === 'true';
}
/**
 * The first entry that can be shown as a picture — the **cover image** a kanban or Cards card
 * draws above its header (`specs/M4-V2-kanban.md` §7.5, `specs/M4-V7-gallery.md` §7.3).
 *
 * It lives beside the value it reads, not in the view: a cover is "what this cell looks like
 * as a picture", which is the column type's question (CLAUDE.md rule 6). `board-view`'s
 * `ColumnTypeUi.coverImageOf` is the browser half and calls this one.
 */
export function firstImageEntry(value) {
    if (value === null)
        return null;
    for (const entry of value.files)
        if (isImageEntry(entry))
            return entry;
    return null;
}
/**
 * The origin `file` URLs are built from. Set once by the api (`APP_URL`); the environment is
 * read as a fallback so a worker, a script or a unit test gets absolute URLs too, exactly as
 * `services/updates.ts` already does for update links.
 */
let assetBaseUrl = null;
export function setAssetBaseUrl(base) {
    assetBaseUrl = base === null || base === undefined || base === '' ? null : base.replace(/\/+$/, '');
}
export function assetBase() {
    if (assetBaseUrl !== null)
        return assetBaseUrl;
    const fromEnv = typeof process !== 'undefined' && typeof process.env === 'object'
        ? (process.env['APP_URL'] ?? '')
        : '';
    return fromEnv.replace(/\/+$/, '');
}
/** Our URL for one entry: the link for a `LINK`, the absolute asset route for an `ASSET`. */
export function fileEntryUrl(entry, ctx) {
    if (entry.linkToFile !== undefined && entry.linkToFile !== '')
        return entry.linkToFile;
    if (entry.assetId === undefined || entry.assetId === null)
        return null;
    return ctx?.assetUrl?.(entry.assetId, entry.name) ?? `${assetBase()}/files/${String(entry.assetId)}`;
}
/**
 * The `ASSET` entry monday's `add_file_to_column` appends — the shape, and only the shape,
 * measured on this account (67 entries): `name`, `assetId`, `isImage`, `fileType`, `createdAt`
 * (epoch **milliseconds**, not ISO) and `createdBy` `[cvs§2 file]`. `isImage` is the **string**
 * `"true"` / `"false"` `[cvs§4.4]`.
 *
 * It lives in the type's own folder because it is the type's own shape: the upload route builds
 * a cell value through this, and never by writing keys of its own (CLAUDE.md rule 6).
 */
export function assetFileEntry(input) {
    const created = input.createdAt instanceof Date ? input.createdAt.getTime() : (input.createdAt ?? Date.now());
    const entry = {
        name: input.name,
        assetId: input.assetId,
        isImage: input.isImage ? 'true' : 'false',
        fileType: 'ASSET',
        createdAt: created,
    };
    if (input.createdBy !== null && input.createdBy !== undefined)
        entry.createdBy = String(input.createdBy);
    return entry;
}
/**
 * The value a cell holds after one more file is attached. Append, never insert: monday's own
 * gallery is upload order. An entry for an `assetId` that is already there **replaces** it, so
 * re-uploading the same asset cannot double it.
 */
export function withFileEntry(value, entry) {
    const key = entry.assetId === undefined ? null : String(entry.assetId);
    const kept = value === null || key === null
        ? (value?.files ?? [])
        : value.files.filter((existing) => String(existing.assetId ?? '') !== key);
    return { files: [...kept, entry] };
}
/** monday's only accepted `file` write payload `[09§A.7 file]`. */
export function isClearAll(raw) {
    if (raw === null || typeof raw !== 'object')
        return false;
    const record = raw;
    if (record.clear_all === true)
        return true;
    const inner = record.files;
    return inner !== null && typeof inner === 'object' && inner.clear_all === true;
}
function normalize(raw) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw !== 'object')
        throw new ColumnValueError('file', `expected an object, received ${typeof raw}`);
    const record = raw;
    // `{"files": {"clear_all": true}}` — the only accepted write `[09§A.7 file]`.
    if (!Array.isArray(record.files)) {
        const inner = record.files;
        if (inner !== null && typeof inner === 'object' && inner.clear_all === true) {
            return null;
        }
        if (record.clear_all === true)
            return null;
        if (record.files === undefined)
            return null;
        /**
         * `{"files": null}` is what monday's own read-back gives for an empty file column on some
         * rows — one real cell ships it (board `824373940`, item `1217365155`, column `files`), and
         * the first trial import failed the whole board on it (T-X2 round 5p). Emptiness is
         * `value === null` (`[cvs§4.1]`), so an explicitly null `files` is an **empty cell**, not a
         * malformed one — the same reading hook 8 took for a cleared `status`. This path is only
         * reachable from `fromMondayValue`: a *write* still goes through `parseValue`, which accepts
         * nothing but `clear_all`.
         */
        if (record.files === null)
            return null;
        throw new ColumnValueError('file', '"files" must be an array');
    }
    const files = [];
    for (const entry of record.files) {
        if (entry === null || typeof entry !== 'object')
            continue;
        const parsed = fileEntrySchema.safeParse(entry);
        if (!parsed.success)
            throw new ColumnValueError('file', 'malformed file entry', parsed.error);
        files.push(parsed.data);
    }
    return files.length === 0 ? null : { files };
}
function renderText(value, ctx) {
    if (value === null)
        return '';
    return value.files
        .map((entry) => fileEntryUrl(entry, ctx))
        .filter((url) => url !== null)
        .join(', ');
}
export const fileColumn = defineColumnType({
    type: 'file',
    title: 'Files',
    description: 'Files and links attached to the item.',
    apiFragmentName: 'FileValue',
    legacyTypeName: 'file',
    idPrefix: 'files',
    valueSchema: fileValueSchema,
    settingsSchema: fileSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: fileSettingsSchema,
    /** Only `add_file_to_column` and `clear_all` write here `[09§A.7 file]`. */
    writable: false,
    supportsSimpleValue: false,
    clearForms: ['null'],
    filterConditions: fileFilterConditions,
    filterOperators: operatorsOf(fileFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    emptyText: '',
    parseValue(raw) {
        if (raw === null || raw === undefined)
            return null;
        // `{"files": {"clear_all": true}}` is the **one** accepted write and it clears `[09§A.7 file]`.
        if (isClearAll(raw))
            return null;
        throw new ColumnValueError('file', 'files are added with add_file_to_column, not by a cell write');
    },
    toText(value, _settings, ctx) {
        return renderText(value, ctx);
    },
    toSortKeys(value, _settings, ctx) {
        if (value === null)
            return { text: null, number: null, date: null };
        // The number shadow is the file **count**, which is what a footer summary wants.
        return { text: textShadow(renderText(value, ctx)), number: value.files.length, date: null };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        if (a.files.length !== b.files.length)
            return false;
        const key = (entry) => `${entry.fileType}|${String(entry.assetId ?? '')}|${entry.linkToFile ?? ''}|${entry.name}`;
        return a.files.every((entry, index) => key(entry) === key(b.files[index]));
    },
    toActivityValue(value) {
        return value === null ? null : { files: value.files.map((entry) => ({ name: entry.name, fileType: entry.fileType })) };
    },
    fromMondayValue(raw) {
        return normalize(raw);
    },
    toMondayValue(value) {
        return value === null ? null : { files: value.files.map((entry) => ({ ...entry })) };
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return fileSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    /**
     * `FileValue.files` is a **union**: `FileAssetValue` | `FileLinkValue` | `FileDocValue` |
     * `FileAssetInvalidValue`. Note `FileLinkValue.file_id` is declared `ID!` but the live API
     * returns null for it, which nulls the whole query `[cvs§5]` — ours makes it nullable and
     * fills it from `fileId` when monday shipped one.
     */
    /**
     * **An empty cell still answers every non-nullable field of its fragment.** monday returns
     * the zero values, not an absent key: recording `0261` answers an unset `dependency` and an
     * unset `board_relation` with `{display_value: "", linked_item_ids: [], linked_items: []}`,
     * `0260` answers an unset `tags` with `{tags: []}` and `0263` answers an unset
     * `time_tracking` with `{running: false, started_at: null, duration: 0, history: []}`.
     * Returning `{}` leaves the non-nullable fields `undefined`, and **one** empty cell then
     * nulls the whole `items` response (T-5s, M9 §12.2a F-1).
     *
     * The three fragment fields that are **rows** rather than projections —
     * `linked_items`, `subitems` and `history` — are resolved by `/v2` itself
     * (`apps/api/src/graphql/v2/column-value-fields.ts`): the registry has no database.
     */
    toApiFragment(value, _settings, ctx) {
        if (value === null)
            return { files: [] };
        return {
            files: value.files.map((entry) => {
                const createdAt = entry.createdAt === undefined ? null : new Date(entry.createdAt).toISOString();
                const creator = entry.createdBy === undefined ? null : String(entry.createdBy);
                if (entry.fileType === 'LINK') {
                    return {
                        __typename: 'FileLinkValue',
                        name: entry.name,
                        url: fileEntryUrl(entry, ctx),
                        file_id: entry.fileId ?? null,
                        kind: 'link',
                        created_at: createdAt,
                        creator_id: creator,
                    };
                }
                if (entry.fileType === 'DOC') {
                    return {
                        __typename: 'FileDocValue',
                        name: entry.name,
                        file_id: entry.fileId ?? null,
                        created_at: createdAt,
                        creator_id: creator,
                    };
                }
                return {
                    __typename: 'FileAssetValue',
                    asset_id: entry.assetId === undefined ? null : String(entry.assetId),
                    name: entry.name,
                    is_image: isImageEntry(entry),
                    created_at: createdAt,
                    creator_id: creator,
                };
            }),
        };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    filterSql(ctx) {
        return defaultFilterSql('text', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map